"use client";

import Link from "next/link";
import { Suspense, useMemo, useRef, useState } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { useInvoiceStore } from "@/lib/invoice/store";
import { saleAmount } from "@/lib/invoice/sales";
import { suggestTag, tagById } from "@/lib/invoice/tags";
import { compareText, fmtMoney, lastDayOfMonth, monthLabel, num, projectLine, todayISO } from "@/lib/invoice/utils";
import { Button, Card, Empty, Field, Input, Loading, Modal, PageHeader, Select, Stat, Textarea, cx, useFeedback } from "@/components/Invoice/ui";
import { TagChip, TagSelect } from "@/components/Invoice/Tags";
import { RowImportButton } from "@/components/Invoice/RowImport";

const blank = (clientId = "") => ({ date: todayISO(), clientId, projectId: "", description: "", qty: 1, rate: "", tag: "", notes: "" });

function SalesInner() {
  const store = useInvoiceStore();
  const router = useRouter();
  const params = useSearchParams();
  const { toast, confirm } = useFeedback();
  const [draft, setDraft] = useState(blank(params.get("client") || ""));
  const [month, setMonth] = useState(todayISO().slice(0, 7));
  const [clientId, setClientId] = useState(params.get("client") || "");
  const [status, setStatus] = useState("unbilled");
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [editing, setEditing] = useState(null);
  const descRef = useRef(null);

  const clientName = useMemo(() => Object.fromEntries(store.clients.map((c) => [c.id, c.name])), [store.clients]);
  const curOf = (cid) => store.clients.find((c) => c.id === cid)?.currency || store.settings.defaults.currency;

  if (!store.ready) return <Loading />;

  const activeClients = [...store.clients].filter((c) => !c.archived).sort((a, b) => a.name.localeCompare(b.name));
  const projectsOf = (cid) =>
    store.projects
      .filter((p) => (p.clientId === cid || (p.kind === "service" && !p.clientId)) && p.status !== "completed")
      .sort((a, b) => compareText(projectLine(a), projectLine(b)));

  const list = store.sales
    .filter((x) => month === "all" || (x.date || "").startsWith(month))
    .filter((x) => !clientId || x.clientId === clientId)
    .filter((x) => status === "all" || (status === "unbilled" ? !x.invoiceId : !!x.invoiceId))
    .filter((x) => [x.description, clientName[x.clientId], x.notes].join(" ").toLowerCase().includes(q.toLowerCase()))
    .sort((a, b) => (b.date || "").localeCompare(a.date || "") || (b.createdAt || "").localeCompare(a.createdAt || ""));

  const unbilledByCur = {};
  const unbilledClients = new Set();
  for (const x of store.sales.filter((s) => !s.invoiceId)) {
    const c = curOf(x.clientId);
    unbilledByCur[c] = (unbilledByCur[c] || 0) + saleAmount(x);
    unbilledClients.add(x.clientId);
  }
  const monthTotalByCur = {};
  for (const x of store.sales.filter((s) => (s.date || "").startsWith(todayISO().slice(0, 7)))) {
    const c = curOf(x.clientId);
    monthTotalByCur[c] = (monthTotalByCur[c] || 0) + saleAmount(x);
  }
  const fmtMulti = (o) => Object.entries(o).map(([c, v]) => fmtMoney(v, c)).join(" + ") || fmtMoney(0, store.settings.defaults.currency);

  const pickProject = (d, projectId) => {
    const p = store.projects.find((x) => x.id === projectId);
    return p ? { ...d, projectId, description: d.description || projectLine(p), rate: d.rate || p.rate, tag: p.tag || d.tag } : { ...d, projectId };
  };

  const add = () => {
    if (!draft.clientId) return toast("Choose a client", { tone: "error" });
    if (!draft.description.trim()) return toast("Describe the sale / work", { tone: "error" });
    if (!num(draft.rate)) return toast("Enter an amount", { tone: "error" });
    store.saveSale({ ...draft, description: draft.description.trim(), qty: num(draft.qty) || 1, rate: num(draft.rate), tag: draft.tag || suggestTag(draft.description, store.settings.tags) });
    toast(`Recorded ${fmtMoney(saleAmount(draft), curOf(draft.clientId))} for ${clientName[draft.clientId]}`, { tone: "success" });
    setDraft({ ...blank(draft.clientId), date: draft.date });
    descRef.current?.focus();
  };

  const toggle = (id) =>
    setSelected((s) => {
      const n = new Set(s);
      n.has(id) ? n.delete(id) : n.add(id);
      return n;
    });

  const invoiceSelected = () => {
    const chosen = store.sales.filter((x) => selected.has(x.id) && !x.invoiceId);
    const clients = new Set(chosen.map((x) => x.clientId));
    if (!chosen.length) return toast("Select unbilled entries", { tone: "error" });
    if (clients.size !== 1) return toast("Select entries for one client at a time (or use the month-end run for everyone).", { tone: "error" });
    router.push(`/invoice/invoices/edit?client=${[...clients][0]}&sales=${chosen.map((x) => x.id).join(",")}`);
  };

  const removeSales = async (ids) => {
    const billed = store.sales.filter((x) => ids.includes(x.id) && x.invoiceId);
    if (billed.length) return toast("Billed entries can’t be deleted – remove them from their invoice first.", { tone: "error" });
    if (!(await confirm(`Delete ${ids.length} entr${ids.length === 1 ? "y" : "ies"}? You can restore from the recycle bin.`, { okLabel: "Delete", danger: true }))) return;
    const tid = store.deleteSales(ids);
    setSelected(new Set());
    setEditing(null);
    toast("Deleted", { action: { label: "Undo", onClick: () => store.restoreTrash(tid) } });
  };

  const months = [...new Set([todayISO().slice(0, 7), ...store.sales.map((x) => (x.date || "").slice(0, 7))])].filter(Boolean).sort().reverse();
  const allSelected = list.length > 0 && list.every((x) => selected.has(x.id));

  return (
    <div>
      <PageHeader
        title="Sales"
        subtitle="Record work and one-off charges as they happen. At month end they’re invoiced together with retainers."
        actions={
          <>
            <RowImportButton kind="sales" defaultClientId={draft.clientId} />
            <Link href="/invoice/invoices?run=1">
              <Button>Month-end invoicing</Button>
            </Link>
          </>
        }
      />

      <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Stat label="Unbilled (all time)" value={fmtMulti(unbilledByCur)} sub={`${store.sales.filter((x) => !x.invoiceId).length} entries · ${unbilledClients.size} client(s)`} tone={Object.keys(unbilledByCur).length ? "red" : undefined} />
        <Stat label={`Recorded in ${monthLabel(todayISO().slice(0, 7))}`} value={fmtMulti(monthTotalByCur)} />
        <Stat label="Billed entries" value={String(store.sales.filter((x) => x.invoiceId).length)} sub="linked to invoices" />
      </div>

      <Card title="Record a sale" className="mb-5">
        <div
          className="grid grid-cols-2 gap-2 md:grid-cols-12"
          onKeyDown={(e) => {
            if (e.key === "Enter" && e.target.tagName !== "TEXTAREA") {
              e.preventDefault();
              add();
            }
          }}
        >
          <Field label="Date" className="md:col-span-2"><Input type="date" value={draft.date} onChange={(e) => setDraft({ ...draft, date: e.target.value })} /></Field>
          <Field label="Client" className="md:col-span-3">
            <Select value={draft.clientId} onChange={(e) => setDraft({ ...draft, clientId: e.target.value, projectId: "" })}>
              <option value="">Choose…</option>
              {activeClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Project (optional)" className="md:col-span-3">
            <Select value={draft.projectId} onChange={(e) => setDraft(pickProject(draft, e.target.value))} disabled={!draft.clientId}>
              <option value="">—</option>
              {projectsOf(draft.clientId).map((p) => <option key={p.id} value={p.id}>{projectLine(p)}</option>)}
            </Select>
          </Field>
          <Field label="Category" className="md:col-span-2">
            <TagSelect className="w-full py-2 text-sm" tags={store.settings.tags} value={draft.tag} onChange={(tag) => setDraft({ ...draft, tag })} placeholder="Auto" />
          </Field>
          <Field label="Qty / hours" className="md:col-span-2"><Input type="number" step="any" value={draft.qty} onChange={(e) => setDraft({ ...draft, qty: e.target.value })} /></Field>
          <Field label="What was sold / done" className="col-span-2 md:col-span-7">
            <Input ref={descRef} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} placeholder="e.g. Extra landing page for Zenvy Wellness" />
          </Field>
          <Field label={`Rate${draft.clientId ? ` (${curOf(draft.clientId)})` : ""}`} className="md:col-span-2">
            <Input type="number" step="0.01" value={draft.rate} onChange={(e) => setDraft({ ...draft, rate: e.target.value })} />
          </Field>
          <div className="flex items-end md:col-span-3">
            <Button className="w-full" onClick={add}>
              Add {num(draft.rate) ? fmtMoney(saleAmount(draft), curOf(draft.clientId)) : ""}
            </Button>
          </div>
        </div>
        <div className="mt-2 text-xs text-slate-500">Press Enter to add. The client stays selected so you can record several entries quickly.</div>
      </Card>

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg border border-slate-200 bg-white p-0.5 text-sm">
          {[["unbilled", "Unbilled"], ["billed", "Billed"], ["all", "All"]].map(([k, l]) => (
            <button key={k} onClick={() => setStatus(k)} className={cx("rounded-md px-3 py-1.5", status === k ? "bg-slate-900 text-white" : "text-slate-600")}>{l}</button>
          ))}
        </div>
        <Select className="max-w-[180px]" value={month} onChange={(e) => setMonth(e.target.value)}>
          <option value="all">All months</option>
          {months.map((m) => <option key={m} value={m}>{monthLabel(m)}</option>)}
        </Select>
        <Select className="max-w-[220px]" value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">All clients</option>
          {activeClients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Input className="max-w-xs" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {selected.size > 0 && (
        <div className="mb-3 flex flex-wrap items-center gap-2 rounded-lg bg-brand-50 px-3 py-2 text-sm">
          <b>{selected.size} selected</b>
          <Button size="sm" onClick={invoiceSelected}>Create invoice now</Button>
          <Button size="sm" variant="danger" onClick={() => removeSales([...selected])}>Delete</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {list.length === 0 ? (
        <Empty title={status === "unbilled" ? "Nothing waiting to be billed" : "No entries"}>
          {store.clients.length ? "Record a sale above – it will appear here until it’s invoiced." : "Add a client first, then record sales for them."}
        </Empty>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200/80 bg-white shadow-card">
          <table className="w-full text-sm">
            <thead className="bg-slate-50/70 text-left text-[11px] font-semibold uppercase tracking-wider text-slate-500">
              <tr>
                <th className="w-10 px-4 py-3">
                  <input type="checkbox" checked={allSelected} onChange={() => setSelected(allSelected ? new Set() : new Set(list.map((x) => x.id)))} />
                </th>
                <th className="px-4 py-3">Date</th>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Description</th>
                <th className="px-4 py-3">Category</th>
                <th className="px-4 py-3 text-right">Amount</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {list.map((x) => (
                <tr key={x.id} className={cx("hover:bg-slate-50", selected.has(x.id) && "bg-brand-50/40")}>
                  <td className="px-4 py-2.5"><input type="checkbox" checked={selected.has(x.id)} onChange={() => toggle(x.id)} /></td>
                  <td className="whitespace-nowrap px-4 py-2.5 tabular-nums text-slate-600">{x.date}</td>
                  <td className="px-4 py-2.5">{clientName[x.clientId] || "—"}</td>
                  <td className="px-4 py-2.5">
                    <div className="font-medium">{x.description}</div>
                    {num(x.qty) !== 1 && <div className="text-xs text-slate-500">{x.qty} × {fmtMoney(num(x.rate), curOf(x.clientId))}</div>}
                  </td>
                  <td className="px-4 py-2.5">{x.tag ? <TagChip tag={tagById(store.settings.tags, x.tag)} /> : <span className="text-xs text-slate-400">—</span>}</td>
                  <td className="whitespace-nowrap px-4 py-2.5 text-right font-semibold tabular-nums">{fmtMoney(saleAmount(x), curOf(x.clientId))}</td>
                  <td className="px-4 py-2.5">
                    {x.invoiceId ? (
                      <Link href={`/invoice/invoices/view?id=${x.invoiceId}`} className="inline-flex rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 hover:underline">
                        Billed · {x.invoiceNumber}
                      </Link>
                    ) : (
                      <span className="inline-flex rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700">Unbilled</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <Button size="sm" variant="secondary" onClick={() => setEditing({ ...x })}>Edit</Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={!!editing}
        onClose={() => setEditing(null)}
        title="Edit sale"
        wide
        footer={
          <>
            {editing && !editing.invoiceId && <Button variant="danger" className="mr-auto" onClick={() => removeSales([editing.id])}>Delete</Button>}
            <Button variant="secondary" onClick={() => setEditing(null)}>Cancel</Button>
            <Button
              onClick={() => {
                store.saveSale({ ...editing, qty: num(editing.qty) || 1, rate: num(editing.rate) });
                setEditing(null);
                toast("Saved", { tone: "success" });
              }}
            >
              Save
            </Button>
          </>
        }
      >
        {editing && (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {editing.invoiceId && (
              <div className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800 sm:col-span-2">
                Already billed on {editing.invoiceNumber}. Changing it here doesn’t change that invoice.
              </div>
            )}
            <Field label="Date"><Input type="date" value={editing.date} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></Field>
            <Field label="Client">
              <Select value={editing.clientId} onChange={(e) => setEditing({ ...editing, clientId: e.target.value, projectId: "" })} disabled={!!editing.invoiceId}>
                {store.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </Select>
            </Field>
            <Field label="Project">
              <Select value={editing.projectId || ""} onChange={(e) => setEditing(pickProject(editing, e.target.value))}>
                <option value="">—</option>
                {projectsOf(editing.clientId).map((p) => <option key={p.id} value={p.id}>{projectLine(p)}</option>)}
              </Select>
            </Field>
            <Field label="Category"><TagSelect className="w-full py-2 text-sm" tags={store.settings.tags} value={editing.tag} onChange={(tag) => setEditing({ ...editing, tag })} /></Field>
            <Field label="Description" className="sm:col-span-2"><Input value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></Field>
            <Field label="Qty / hours"><Input type="number" step="any" value={editing.qty} onChange={(e) => setEditing({ ...editing, qty: e.target.value })} /></Field>
            <Field label="Rate"><Input type="number" step="0.01" value={editing.rate} onChange={(e) => setEditing({ ...editing, rate: e.target.value })} /></Field>
            <Field label="Internal notes" className="sm:col-span-2"><Textarea value={editing.notes || ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} /></Field>
          </div>
        )}
      </Modal>

      <div className="mt-4 text-xs text-slate-500">
        At month end, <Link className="text-brand-700 underline" href="/invoice/invoices?run=1">Month-end invoicing</Link> creates one draft invoice per client with their retainers and every
        unbilled sale up to {lastDayOfMonth(todayISO().slice(0, 7))}.
      </div>
    </div>
  );
}

export default function SalesPage() {
  return (
    <Suspense fallback={<Loading />}>
      <SalesInner />
    </Suspense>
  );
}
